import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";
import { Input as AntInput } from "antd";
import { useSelector } from "react-redux";

import type { RootState } from "../../../../store";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { buildGamePassImageUrl } from "../../../../utils/gamePassImageUpload";
import {
  Button,
  FileDropzone,
  Input,
  Modal,
  Select,
} from "../../../shared";

import {
  BET_CURRENCY_OPTIONS,
  BET_TYPE_OPTIONS,
  CASINO_ACTION_OPTIONS,
  DEFAULT_CASINO_ACTION,
  DEFAULT_SOCIAL_TASK_TYPE,
  TASK_DESCRIPTION_MAX,
  SOCIAL_TASK_TYPE_OPTIONS,
  TASK_QUALIFIER_OPTIONS,
  TASK_TYPE_OPTIONS,
  betTypeRequiresLegCount,
  createEmptyTask,
  getMissingTaskRequirements,
  isCurrencyMinBetAboveFloor,
  isTaskDraftValid,
  MIN_LEG_COUNT,
  sanitizeDecimalInput,
  sanitizeIntegerInput,
  sanitizeSignedDecimalInput,
  type BetCurrency,
  type BetType,
  type CasinoAction,
  type SocialTaskType,
  type SportOption,
  type TaskDraft,
  type TaskQualifier,
  type TaskRequirementKey,
  type TaskTriggerDraft,
  type TaskType,
  CASINO_TASK_SUBMISSION_MODE,
  resolveSubmittedTaskSubmissionMode,
  stampTaskSubmissionModeAtSave,
  resolveTaskSubmissionModeFromRecord,
  type TaskSubmissionModeDefaults,
} from "../createSeasonData";
import type { LabeledOption } from "../../../../queries/game-pass/gamePassOptions";

import CasinoProviderSelect from "./CasinoProviderSelect";
import SportSpecificSelect from "./SportSpecificSelect";
import SubmissionModeField from "../../shared/SubmissionModeField";
import styles from "./CreateTaskModal.module.css";

const { TextArea } = AntInput;

/** Maps each required-field key to the label the admin sees on that field, so a
 *  disabled Create/Save CTA can spell out exactly what's still missing. */
const TASK_REQUIREMENT_LABELS: Record<TaskRequirementKey, string> = {
  name: AppStrings.CREATE_TASK_TASK_NAME,
  minBetAmount: AppStrings.CREATE_TASK_MIN_BET_AMOUNT,
  betCurrencies: AppStrings.CREATE_TASK_BET_CURRENCY,
  qualifier: AppStrings.CREATE_TASK_QUALIFIER,
  minLegCount: AppStrings.CREATE_TASK_NUMBER_OF_LEGS,
  xpAmount: AppStrings.CREATE_TASK_XP_AMOUNT,
  casinoSelection: AppStrings.CREATE_TASK_CASINO_PROVIDERS,
  casinoActionAmount: AppStrings.CREATE_TASK_ACTION_AMOUNT,
  casinoBetCurrencies: AppStrings.CREATE_TASK_BET_CURRENCY,
  socialAmount: AppStrings.CREATE_TASK_X_AMOUNT,
};

/**
 * Operating mode for the Task modal.
 *
 *  - `undefined` (default) — legacy behaviour. The modal flips between
 *    create / edit purely off whether `initialValues` is supplied. Every
 *    existing call site keeps working unchanged.
 *  - `"view"` — read-only preview opened from the eye icon on a saved
 *    task (collapsed Challenge card in the wizard, locked Challenge card
 *    in Season Details). Title becomes "Task", the footer is hidden, and
 *    every input / pill / dropzone is rendered disabled so the user can
 *    inspect-but-not-mutate.
 */
export type CreateTaskModalMode = "view";

export type CreateTaskModalProps = {
  open: boolean;
  /** 1-based position so generated ids stay unique against other tasks
   *  added in the same challenge in the same ms. */
  taskIndex: number;
  /** Optional task draft to seed the form with (edit mode); when omitted the
   *  modal opens with the default empty task. When provided, the modal flips
   *  to edit-mode UI: the title becomes "Edit Task", the primary CTA reads
   *  "Save Task", and `onDelete` (if passed) is rendered as a left-aligned
   *  destructive button. */
  initialValues?: TaskDraft;
  /** Optional explicit mode override. Set to `"view"` for the read-only
   *  preview reached from the eye icon — every editing affordance is hidden
   *  in that case. When omitted, the modal infers create / edit from
   *  `initialValues`. */
  mode?: CreateTaskModalMode;
  onClose: () => void;
  onSubmit: (next: TaskDraft) => void;
  /** Only meaningful in edit mode. When omitted the Delete button is hidden
   *  so the modal can still be used for plain "view-only" pre-fills. */
  onDelete?: () => void;
  /** When `true`, the primary CTA renders disabled regardless of the
   *  draft validity. Owned by the parent so it can block double-clicks
   *  while an in-flight create / save API call settles — see
   *  `ChallengeCard.handleTaskSubmit`. Defaults to `false`. */
  isSubmitting?: boolean;
  /** When set (non-empty), the "Same XP reward for all tasks" toggle is on at
   *  the season level: every task's XP Amount field is locked to this value
   *  (rendered disabled and pre-filled) so the admin can't diverge per-task.
   *  The seeded value flows through to the submitted draft. Omit / empty to
   *  keep the per-task XP field freely editable. */
  forcedXpAmount?: string;
  /** Season-level submission defaults (Step 1 / General). New tasks inherit
   *  these until the admin picks a per-task override in the modal. */
  defaultSubmissionModes?: TaskSubmissionModeDefaults;
  // ─── API-driven option catalogues ────────────────────────────────────────
  // Each defaults to the bundled static catalogue so existing call sites (and
  // the modal's unit tests) keep working unchanged. The parent (`ChallengeCard`)
  // passes the backend-derived lists (`GET /config` + `GET /task-options`) so
  // the dropdowns reflect the operator's live configuration.
  /** Task Type pills (Sportsbook / Socials / Casino). */
  taskTypeOptions?: LabeledOption<TaskType>[];
  /** Type-of-Bet pills. */
  betTypeOptions?: LabeledOption<BetType>[];
  /** Bet Currency multi-select options (sportsbook + casino). */
  betCurrencyOptions?: LabeledOption<BetCurrency>[];
  /** Per-currency minimum bet amounts from `GET /config`, keyed by
   *  `BetCurrency`. Enforces the sportsbook "Minimum Bet Amount" floor (e.g.
   *  100 when Rebet Coins is selected) and drives the inline hint beneath the
   *  input. Omit to leave the field unconstrained (static / unit-test usage). */
  betCurrencyMinimums?: Partial<Record<BetCurrency, number>>;
  /** Qualifier pills. */
  qualifierOptions?: LabeledOption<TaskQualifier>[];
  /** Socials "Task Type" dropdown options. */
  socialTaskTypeOptions?: LabeledOption<SocialTaskType>[];
  /** Sport Specific picker catalogue. */
  sportOptions?: ReadonlyArray<SportOption>;
};

/**
 * Back-fills the per-currency Minimum Bet Amount map for an edited task that
 * predates the per-currency inputs: such a draft only carries the legacy single
 * `minBetAmount`, so we mirror it onto every selected Bet Currency the first
 * time the modal opens (only when no per-currency value exists yet). Freshly
 * authored tasks start empty and are unaffected.
 */
const seedMinBetByCurrency = (trigger: TaskTriggerDraft): TaskTriggerDraft => {
  const currencies = trigger.betCurrencies;
  if (currencies.length === 0) return trigger;
  const existing = trigger.minBetByCurrency ?? {};
  const hasAnyValue = currencies.some(
    (currency) => (existing[currency] ?? "").trim() !== "",
  );
  if (hasAnyValue) return trigger;
  const legacy = (trigger.minBetAmount ?? "").trim();
  if (legacy === "") return trigger;
  const seeded: Partial<Record<BetCurrency, string>> = { ...existing };
  for (const currency of currencies) seeded[currency] = legacy;
  return { ...trigger, minBetByCurrency: seeded };
};

const buildInitialDraft = (
  taskIndex: number,
  initialValues: TaskDraft | undefined,
  forcedXpAmount: string | undefined,
): TaskDraft => {
  const source = initialValues ?? createEmptyTask(taskIndex);
  const seededTrigger = seedMinBetByCurrency(source.trigger);
  // Season defaults pre-fill the radio via `resolveTaskSubmissionModeFromRecord`
  // but are not stored on the draft until Save stamps the selected value.
  const trigger: TaskTriggerDraft = {
    ...seededTrigger,
    taskSubmissionMode:
      seededTrigger.taskType === "SOCIALS"
        ? undefined
        : seededTrigger.taskType === "CASINO"
          ? CASINO_TASK_SUBMISSION_MODE
          : seededTrigger.taskSubmissionMode,
  };

  const base: TaskDraft = {
    ...source,
    trigger,
  };
  if (!forcedXpAmount) return base;
  // Lock the per-task XP to the season-level value so the draft the parent
  // ultimately receives carries the shared amount, not whatever the task had.
  return {
    ...base,
    trigger: { ...base.trigger, xpAmount: forcedXpAmount },
  };
};

/**
 * Modal that lets the admin author a single Task for a Challenge. The form
 * is fully local — pressing Create emits the final `TaskDraft` so the
 * parent can append it to the challenge. Nothing is persisted to the API
 * yet (see Create Season UI-only spec).
 */
const CreateTaskModal: FC<CreateTaskModalProps> = ({
  open,
  taskIndex,
  initialValues,
  mode,
  onClose,
  onSubmit,
  onDelete,
  isSubmitting = false,
  forcedXpAmount,
  defaultSubmissionModes,
  taskTypeOptions,
  betTypeOptions,
  betCurrencyOptions: betCurrencyOptionsProp,
  betCurrencyMinimums,
  qualifierOptions,
  socialTaskTypeOptions: socialTaskTypeOptionsProp,
  sportOptions,
}) => {
  // Edit-mode is driven purely off the presence of `initialValues` — the
  // parent owns the lifecycle and decides whether the modal is in "create"
  // or "edit" mode by passing (or omitting) a draft to seed. The explicit
  // `mode="view"` opt-in supersedes that auto-detection for the read-only
  // preview surface (eye-icon path on saved tasks).
  const isViewMode = mode === "view";
  const isEditMode = !isViewMode && initialValues !== undefined;
  // Season-level "Same XP reward for all tasks" lock. Never applies in the
  // read-only view modal (which shows the task's own saved XP verbatim).
  const isXpLocked = !isViewMode && Boolean(forcedXpAmount);

  // Odds format is platform-driven: the `odds_type` config ("american" |
  // "decimal") decides how the Min/Max Odds inputs behave and which format hint
  // the admin sees. Defaults to American (the historical behaviour and the
  // format the BE `*_american` fields expect) when the config is absent.
  const oddsType = useSelector(
    (state: RootState) =>
      state.config?.appConfig?.app_currency_config?.odds_type,
  );
  const isDecimalOdds = oddsType === "decimal";
  // Decimal odds are always positive (>= 1.0), so they use the unsigned
  // sanitizer; American odds keep the signed sanitizer (+150 / -110).
  const sanitizeOddsInput = isDecimalOdds
    ? sanitizeDecimalInput
    : sanitizeSignedDecimalInput;
  const minOddsPlaceholder = isDecimalOdds
    ? AppStrings.CREATE_TASK_MIN_ODDS_PLACEHOLDER_DECIMAL
    : AppStrings.CREATE_TASK_MIN_ODDS_PLACEHOLDER;
  const maxOddsPlaceholder = isDecimalOdds
    ? AppStrings.CREATE_TASK_MAX_ODDS_PLACEHOLDER_DECIMAL
    : AppStrings.CREATE_TASK_MAX_ODDS_PLACEHOLDER;
  const oddsFormatHint = isDecimalOdds
    ? AppStrings.CREATE_TASK_ODDS_FORMAT_HINT_DECIMAL
    : AppStrings.CREATE_TASK_ODDS_FORMAT_HINT_AMERICAN;
  const [draft, setDraft] = useState<TaskDraft>(() =>
    buildInitialDraft(
      taskIndex,
      initialValues,
      isViewMode ? undefined : forcedXpAmount,
    ),
  );

  // Conditions are progressive — each "+" pill in the figma adds a new
  // input. We track which ones the user has revealed locally so the modal
  // can hide unused fields without losing in-flight values on re-open.
  const [visibleConditions, setVisibleConditions] = useState<{
    minOdds: boolean;
    maxOdds: boolean;
    sportSpecific: boolean;
    streak: boolean;
  }>(() => ({
    minOdds: Boolean(initialValues?.trigger?.oddsRequirement),
    maxOdds: Boolean(initialValues?.trigger?.maxOddsRequirement),
    sportSpecific: Boolean(
      initialValues?.trigger?.sportSpecific?.length ||
        initialValues?.trigger?.sportLeagues?.length,
    ),
    streak: Boolean(initialValues?.trigger?.streak),
  }));

  // Seed the form once per modal open + task identity. Parent re-renders
  // (live `editingTask` lookup, season defaults, forced XP) must not reset
  // an in-flight Manual / Auto override the admin picked but hasn't saved yet.
  const seededTaskKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      seededTaskKeyRef.current = null;
      return;
    }
    const seedKey = `${isViewMode ? "view" : isEditMode ? "edit" : "create"}:${
      initialValues?.id ?? `new-${taskIndex}`
    }`;
    if (seededTaskKeyRef.current === seedKey) return;
    seededTaskKeyRef.current = seedKey;
    setDraft(
      buildInitialDraft(
        taskIndex,
        initialValues,
        isViewMode ? undefined : forcedXpAmount,
      ),
    );
    setVisibleConditions({
      minOdds: Boolean(initialValues?.trigger?.oddsRequirement),
      maxOdds: Boolean(initialValues?.trigger?.maxOddsRequirement),
      sportSpecific: Boolean(
        initialValues?.trigger?.sportSpecific?.length ||
          initialValues?.trigger?.sportLeagues?.length,
      ),
      streak: Boolean(initialValues?.trigger?.streak),
    });
  }, [
    open,
    taskIndex,
    initialValues?.id,
    isEditMode,
    isViewMode,
    forcedXpAmount,
    initialValues,
  ]);

  const update = useCallback((patch: Partial<TaskDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
  }, []);

  const updateTrigger = useCallback((patch: Partial<TaskTriggerDraft>) => {
    setDraft((current) => ({
      ...current,
      trigger: { ...current.trigger, ...patch },
    }));
  }, []);

  // Apply a Bet Currency selection change while pruning the per-currency
  // Minimum Bet Amount map down to the currencies that remain selected, so a
  // de-selected currency never leaves a stale (and potentially submit-blocking)
  // minimum behind.
  const handleBetCurrenciesChange = useCallback(
    (next: BetCurrency[] | undefined) => {
      const selected = next ?? [];
      setDraft((current) => {
        const prevMap = current.trigger.minBetByCurrency ?? {};
        const prunedMap: Partial<Record<BetCurrency, string>> = {};
        for (const currency of selected) {
          if (prevMap[currency] !== undefined) {
            prunedMap[currency] = prevMap[currency];
          }
        }
        return {
          ...current,
          trigger: {
            ...current.trigger,
            betCurrencies: selected,
            minBetByCurrency: prunedMap,
          },
        };
      });
    },
    [],
  );

  const updateMinBetForCurrency = useCallback(
    (currency: BetCurrency, value: string) => {
      setDraft((current) => ({
        ...current,
        trigger: {
          ...current.trigger,
          minBetByCurrency: {
            ...current.trigger.minBetByCurrency,
            [currency]: value,
          },
        },
      }));
    },
    [],
  );

  // Each option list prefers the API-derived list passed by the parent and
  // falls back to the bundled static catalogue (so isolated usage / unit tests
  // keep their existing behaviour without wiring the queries).
  const betCurrencyOptions = useMemo(
    () =>
      betCurrencyOptionsProp ??
      BET_CURRENCY_OPTIONS.map((option) => ({
        value: option.value,
        label: AppStrings[option.labelKey],
      })),
    [betCurrencyOptionsProp],
  );

  const resolvedTaskTypeOptions = useMemo(
    () =>
      taskTypeOptions ??
      TASK_TYPE_OPTIONS.map((option) => ({
        value: option.value,
        label: AppStrings[option.labelKey],
      })),
    [taskTypeOptions],
  );

  const resolvedBetTypeOptions = useMemo(
    () =>
      betTypeOptions ??
      BET_TYPE_OPTIONS.map((option) => ({
        value: option.value,
        label: AppStrings[option.labelKey],
      })),
    [betTypeOptions],
  );

  const resolvedQualifierOptions = useMemo(
    () =>
      qualifierOptions ??
      TASK_QUALIFIER_OPTIONS.map((option) => ({
        value: option.value,
        label: AppStrings[option.labelKey],
      })),
    [qualifierOptions],
  );

  // Hide a revealed "More Conditions" card AND clear the draft field(s) it
  // owns, so a removed condition never leaks a stale value into the
  // submitted draft (and never blocks submit via a left-behind invalid odds
  // string).
  const removeCondition = useCallback(
    (key: "minOdds" | "maxOdds" | "sportSpecific" | "streak") => {
      setVisibleConditions((prev) => ({ ...prev, [key]: false }));
      const clears: Record<typeof key, Partial<TaskTriggerDraft>> = {
        minOdds: { oddsRequirement: undefined },
        maxOdds: { maxOddsRequirement: undefined },
        sportSpecific: { sportSpecific: undefined, sportLeagues: undefined },
        streak: { streak: undefined },
      };
      updateTrigger(clears[key]);
    },
    [updateTrigger],
  );

  // Reveal the Minimum Odds card. On American platforms we seed a leading "-"
  // so the admin immediately sees that negative odds are supported (e.g.
  // "-110"); the seed is only applied when the field is empty so re-opening (or
  // an edit-mode pre-fill) never clobbers an existing value, and the admin can
  // delete the "-" to enter a positive value. A lone "-" is tolerated by the
  // validator and parsed to `null` by the mapper, so it never ships a bad odd.
  // Decimal odds are always unsigned (>= 1.0), so seeding "-" would contradict
  // the decimal format hint — leave the field empty in that mode.
  const revealMinOdds = useCallback(() => {
    setVisibleConditions((prev) => ({ ...prev, minOdds: true }));
    if (isDecimalOdds) return;
    setDraft((current) =>
      current.trigger.oddsRequirement
        ? current
        : {
            ...current,
            trigger: { ...current.trigger, oddsRequirement: "-" },
          },
    );
  }, [isDecimalOdds]);

  const handleSubmit = useCallback(() => {
    if (!isTaskDraftValid(draft, betCurrencyMinimums)) return;
    if (isSubmitting) return;
    onSubmit(stampTaskSubmissionModeAtSave(draft, defaultSubmissionModes));
  }, [
    draft,
    betCurrencyMinimums,
    defaultSubmissionModes,
    isSubmitting,
    onSubmit,
  ]);

  // Either the form is invalid OR the parent is waiting on an API call —
  // both states disable the CTA so the user can't double-submit while
  // `useCreateTask`'s `mutateAsync` is in flight.
  const isSubmitDisabled =
    !isTaskDraftValid(draft, betCurrencyMinimums) || isSubmitting;
  const descriptionLength = draft.description.length;

  // Required-but-empty fields, mapped to their on-screen labels. Surfaced as an
  // inline hint next to a disabled CTA so admins (notably when editing an older
  // task saved before a field became mandatory) know what to fill in rather
  // than facing a greyed-out button with no explanation.
  const missingRequirementLabels = useMemo(
    () =>
      getMissingTaskRequirements(draft).map(
        (key) => TASK_REQUIREMENT_LABELS[key],
      ),
    [draft],
  );

  // Resolve a Bet Currency value to its display label (e.g. "Rebet Coins") so
  // each per-currency Minimum Bet Amount input can be titled with the currency
  // it constrains.
  const betCurrencyLabelMap = useMemo(
    () => new Map(betCurrencyOptions.map((option) => [option.value, option.label])),
    [betCurrencyOptions],
  );

  // One descriptor per selected sportsbook Bet Currency, driving its dedicated
  // Minimum Bet Amount input: the current value, the configured floor hint
  // (e.g. "Minimum 100 Rebet Coins required" from `GET /config`) and whether
  // the typed amount is below that floor (error tone). An empty field is
  // "missing" (handled by the required-fields hint), not "below minimum".
  const minBetCurrencyFields = useMemo(
    () =>
      draft.trigger.betCurrencies.map((currency) => {
        const label = betCurrencyLabelMap.get(currency) ?? currency;
        const value = draft.trigger.minBetByCurrency?.[currency] ?? "";
        const min = betCurrencyMinimums?.[currency];
        const hasFloor =
          typeof min === "number" && Number.isFinite(min) && min > 0;
        const hintText = hasFloor
          ? `${AppStrings.CREATE_TASK_MIN_BET_AMOUNT_FLOOR_PREFIX} ${min} ${label} ${AppStrings.CREATE_TASK_MIN_BET_AMOUNT_FLOOR_SUFFIX}`
          : null;
        const belowFloor =
          hasFloor &&
          value.trim() !== "" &&
          !isCurrencyMinBetAboveFloor(value, currency, betCurrencyMinimums);
        return { currency, label, value, hintText, belowFloor };
      }),
    [
      draft.trigger.betCurrencies,
      draft.trigger.minBetByCurrency,
      betCurrencyMinimums,
      betCurrencyLabelMap,
    ],
  );

  // Sportsbook, Socials and Casino each surface their own Trigger fields
  // below the Task Type pills. The bet-specific inputs only make sense for
  // sportsbook tasks, the provider/game + Action picker only for casino
  // tasks, and the Task Type dropdown + X Amount only for socials tasks.
  const isSportsbook = draft.trigger.taskType === "SPORTSBOOK";
  const sportsbookSubmissionValue =
    resolveTaskSubmissionModeFromRecord(
      "SPORTSBOOK",
      draft.trigger.taskSubmissionMode,
      defaultSubmissionModes,
    ) ?? "AUTO";
  // Sport Specific spans the full grid width only when it's the sole revealed
  // condition; alongside any other condition it sits at the default 50% so the
  // two-column grid stays aligned.
  const isSportSpecificOnlyCondition =
    visibleConditions.sportSpecific &&
    Object.values(visibleConditions).filter(Boolean).length === 1;
  // The Number of Legs input (→ `min_leg_count`) only makes sense for the
  // multi-leg bet types (Parlay / SGP); every other bet type hides it (and its
  // value is cleared on switch, see `updateTrigger` usage on the Type of Bet
  // pills).
  const showNumberOfLegs = betTypeRequiresLegCount(draft.trigger.betType);
  // A parlay / SGP needs at least MIN_LEG_COUNT legs — surface an inline error
  // when the admin has typed a value that's below the minimum (an empty field
  // is "missing", handled by the required-fields hint, not "below minimum").
  const legCountBelowMin = useMemo(() => {
    if (!showNumberOfLegs) return false;
    const trimmed = (draft.trigger.minLegCount ?? "").trim();
    if (trimmed === "") return false;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) && parsed < MIN_LEG_COUNT;
  }, [showNumberOfLegs, draft.trigger.minLegCount]);
  const isCasino = draft.trigger.taskType === "CASINO";
  const isSocials = draft.trigger.taskType === "SOCIALS";
  // The "Number of Bets" input and the Streak card both drive the single
  // sportsbook `target_value`, so they're mutually exclusive: a value in one
  // disables (and hides the reveal pill for) the other.
  const hasNumberOfBets = (draft.trigger.numberOfBets ?? "").trim() !== "";
  const hasStreak = (draft.trigger.streak ?? "").trim() !== "";

  const socialTaskTypeOptions = useMemo(
    () =>
      socialTaskTypeOptionsProp ??
      SOCIAL_TASK_TYPE_OPTIONS.map((option) => ({
        value: option.value,
        label: AppStrings[option.labelKey],
      })),
    [socialTaskTypeOptionsProp],
  );

  const modalTitle = (() => {
    if (isViewMode) return AppStrings.VIEW_TASK;
    if (isEditMode) return AppStrings.EDIT_TASK;
    return AppStrings.CREATE_TASK;
  })();

  // View mode is read-only — passing `footer={null}` to Modal removes the
  // Save / Delete bar entirely, leaving only the X close affordance.
  const modalFooter = isViewMode ? null : (
    <div className={styles.footer}>
      {missingRequirementLabels.length > 0 && (
        <p
          className={styles.requiredHint}
          data-testid="create-task-missing-requirements"
        >
          {AppStrings.CREATE_WIZARD_REQUIRED_FIELDS_HINT}{" "}
          {missingRequirementLabels.join(", ")}
        </p>
      )}
      {isEditMode && onDelete && (
        <Button
          intent="danger"
          variant="outline"
          onClick={onDelete}
          data-testid="create-task-delete"
        >
          {AppStrings.EDIT_TASK_DELETE}
        </Button>
      )}
      <Button
        intent="primary"
        onClick={handleSubmit}
        disabled={isSubmitDisabled}
        loading={isSubmitting}
        data-testid="create-task-submit"
      >
        {isEditMode
          ? AppStrings.EDIT_TASK_SUBMIT
          : AppStrings.CREATE_TASK_SUBMIT}
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={modalTitle}
      size="xl"
      data-testid="create-task-modal"
      footer={modalFooter}
    >
      <div className={styles.body}>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {AppStrings.CREATE_TASK_GENERAL}
          </h3>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              {AppStrings.CREATE_TASK_TASK_NAME}
            </span>
            <Input
              value={draft.name}
              placeholder={AppStrings.CREATE_TASK_TASK_NAME_PLACEHOLDER}
              onChange={(e) => update({ name: e.target.value })}
              disabled={isViewMode}
              data-testid="create-task-name"
            />
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              {AppStrings.CREATE_TASK_DESCRIPTION}
            </span>
            <div className={styles.textareaWrapper}>
              <TextArea
                className="custom-form-input"
                value={draft.description}
                placeholder={AppStrings.CREATE_TASK_DESCRIPTION_PLACEHOLDER}
                autoSize={{ minRows: 2, maxRows: 4 }}
                maxLength={TASK_DESCRIPTION_MAX}
                onChange={(e) => update({ description: e.target.value })}
                disabled={isViewMode}
                data-testid="create-task-description"
              />
              <span
                className={styles.charCount}
                aria-label={AppStrings.CREATE_TASK_DESCRIPTION_COUNTER_ARIA}
                data-testid="create-task-description-count"
              >
                {`${descriptionLength}/${TASK_DESCRIPTION_MAX}`}
              </span>
            </div>
          </label>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>
              {AppStrings.CREATE_TASK_UPLOAD_IMAGE}
            </span>
            <FileDropzone
              value={draft.imageFile ?? null}
              onChange={(file) => update({ imageFile: file ?? undefined })}
              accept="image/*"
              initialUrl={draft.imageUrl ?? buildGamePassImageUrl(draft.imgKey)}
              onRemoveInitial={() => update({ imgKey: null, imageUrl: null })}
              hint={AppStrings.CHALLENGE_ICON_HINT}
              buttonLabel={AppStrings.CHALLENGE_ICON_UPLOAD}
              disabled={isViewMode}
              className={styles.imageDropzone}
              data-testid="create-task-image-dropzone"
            />
          </div>
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {AppStrings.CREATE_TASK_TRIGGER}
          </h3>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>
              {`${AppStrings.CREATE_TASK_TASK_TYPE} *`}
            </span>
            <PillGroup<TaskType>
              value={draft.trigger.taskType}
              options={resolvedTaskTypeOptions}
              onChange={(value) =>
                updateTrigger({
                  taskType: value,
                  ...(value === "SOCIALS"
                    ? { taskSubmissionMode: undefined }
                    : value === "CASINO"
                      ? { taskSubmissionMode: CASINO_TASK_SUBMISSION_MODE }
                      : value === "SPORTSBOOK"
                        ? { taskSubmissionMode: undefined }
                        : {}),
                })
              }
              disabled={isViewMode}
              testId="create-task-task-type"
            />
          </div>

          {/* Each Task Type renders its own trigger fields below the pills:
              Sportsbook here, Casino and Socials in the blocks that follow. */}
          {isSportsbook && (
            <>
              <div className={styles.twoColumn}>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>
                    {`${AppStrings.CREATE_TASK_BET_CURRENCY} *`}
                  </span>
                  <Select<BetCurrency[]>
                    mode="multiple"
                    value={[...draft.trigger.betCurrencies]}
                    options={betCurrencyOptions}
                    placeholder={
                      AppStrings.CREATE_TASK_BET_CURRENCY_PLACEHOLDER
                    }
                    onChange={(value) => handleBetCurrenciesChange(value)}
                    disabled={isViewMode}
                    data-testid="create-task-bet-currency"
                  />
                </label>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>
                    {`${AppStrings.CREATE_TASK_TYPE_OF_BET} *`}
                  </span>
                  <PillGroup<BetType>
                    value={draft.trigger.betType}
                    options={resolvedBetTypeOptions}
                    onChange={(value) =>
                      // Clear any Number of Legs value when leaving a multi-leg
                      // bet type so a stale leg count never ships for a bet type
                      // that doesn't use one.
                      updateTrigger(
                        betTypeRequiresLegCount(value)
                          ? { betType: value }
                          : { betType: value, minLegCount: undefined },
                      )
                    }
                    disabled={isViewMode}
                    testId="create-task-bet-type"
                  />
                </div>
              </div>

              {/* Number of Legs (→ `min_leg_count`) shows for the multi-leg
                  bet types (Parlay / SGP). */}
              {showNumberOfLegs && (
                <label
                  className={styles.field}
                  data-testid="create-task-number-of-legs-field"
                >
                  <span className={styles.fieldLabel}>
                    {`${AppStrings.CREATE_TASK_NUMBER_OF_LEGS} *`}
                  </span>
                  <Input
                    value={draft.trigger.minLegCount ?? ""}
                    inputMode="numeric"
                    tone={legCountBelowMin ? "error" : "default"}
                    placeholder={
                      AppStrings.CREATE_TASK_NUMBER_OF_LEGS_PLACEHOLDER
                    }
                    onChange={(e) =>
                      updateTrigger({
                        minLegCount: sanitizeIntegerInput(e.target.value),
                      })
                    }
                    disabled={isViewMode}
                    data-testid="create-task-number-of-legs"
                  />
                  {legCountBelowMin && (
                    <span
                      className={`${styles.minBetHint} ${styles.minBetHintError}`}
                      data-testid="create-task-number-of-legs-error"
                    >
                      {AppStrings.CREATE_TASK_NUMBER_OF_LEGS_MIN_HINT}
                    </span>
                  )}
                </label>
              )}

              <div className={styles.twoColumn}>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>
                    {`${AppStrings.CREATE_TASK_MIN_BET_AMOUNT} *`}
                  </span>
                  {/* One Minimum Bet Amount input per selected Bet Currency so
                      the admin can set a distinct floor for each (e.g. 100 Rebet
                      Coins vs 0.5 Rebet Cash). Until a currency is picked there's
                      nothing to constrain, so a disabled prompt stands in. */}
                  {minBetCurrencyFields.length === 0 ? (
                    <Input
                      value=""
                      inputMode="decimal"
                      placeholder={
                        AppStrings.CREATE_TASK_MIN_BET_AMOUNT_PLACEHOLDER
                      }
                      disabled
                      data-testid="create-task-min-bet-amount"
                    />
                  ) : (
                    <div
                      className={styles.minBetCurrencyList}
                      data-testid="create-task-min-bet-amount"
                    >
                      {minBetCurrencyFields.map((field) => (
                        <div
                          key={field.currency}
                          className={styles.minBetCurrencyRow}
                          data-testid={`create-task-min-bet-row-${field.currency}`}
                        >
                          <Input
                            value={field.value}
                            inputMode="decimal"
                            tone={field.belowFloor ? "error" : "default"}
                            suffix={
                              <span className={styles.minBetCurrencySuffix}>
                                {field.label}
                              </span>
                            }
                            placeholder={
                              AppStrings.CREATE_TASK_MIN_BET_AMOUNT_PLACEHOLDER
                            }
                            onChange={(e) =>
                              updateMinBetForCurrency(
                                field.currency,
                                sanitizeDecimalInput(e.target.value),
                              )
                            }
                            disabled={isViewMode}
                            data-testid={`create-task-min-bet-input-${field.currency}`}
                          />
                          {field.hintText && (
                            <span
                              className={`${styles.minBetHint}${
                                field.belowFloor
                                  ? ` ${styles.minBetHintError}`
                                  : ""
                              }`}
                              data-testid={`create-task-min-bet-hint-${field.currency}`}
                            >
                              {field.hintText}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>
                    {`${AppStrings.CREATE_TASK_QUALIFIER} *`}
                  </span>
                  <PillGroup<TaskQualifier>
                    value={draft.trigger.qualifier}
                    options={resolvedQualifierOptions}
                    onChange={(value) => updateTrigger({ qualifier: value })}
                    disabled={isViewMode}
                    testId="create-task-qualifier"
                  />
                </div>
              </div>

              {/* Number of Bets (→ the task's `target_value` when
                  `counting_method` is "COUNT"). Mutually exclusive with the
                  Streak card — both write the single `target_value`, so a
                  Streak value disables this input. */}
              <label
                className={styles.field}
                data-testid="create-task-number-of-bets-field"
              >
                <span className={styles.fieldLabel}>
                  {AppStrings.CREATE_TASK_NUMBER_OF_BETS}
                </span>
                <Input
                  value={draft.trigger.numberOfBets ?? ""}
                  inputMode="numeric"
                  placeholder={
                    AppStrings.CREATE_TASK_NUMBER_OF_BETS_PLACEHOLDER
                  }
                  onChange={(e) =>
                    updateTrigger({
                      numberOfBets: sanitizeIntegerInput(e.target.value),
                    })
                  }
                  disabled={isViewMode || hasStreak}
                  data-testid="create-task-number-of-bets"
                />
              </label>

              <div className={styles.field}>
                {/* The "More Conditions" header + reveal pills only make
                    sense when the user is authoring the task. In view mode
                    we keep the populated condition inputs (so all configured
                    trigger data is visible) but hide the reveal row. */}
                {!isViewMode && (
                  <>
                    <span className={styles.fieldLabel}>
                      {AppStrings.CREATE_TASK_MORE_CONDITIONS}
                    </span>
                    <div className={styles.conditionRow}>
                      {!visibleConditions.minOdds && (
                        <button
                          type="button"
                          className={styles.conditionPill}
                          onClick={revealMinOdds}
                          data-testid="create-task-condition-min-odds"
                        >
                          <span
                            className={styles.conditionPillIcon}
                            aria-hidden="true"
                          >
                            +
                          </span>
                          {AppStrings.CREATE_TASK_MIN_ODDS}
                        </button>
                      )}
                      {!visibleConditions.maxOdds && (
                        <button
                          type="button"
                          className={styles.conditionPill}
                          onClick={() =>
                            setVisibleConditions((prev) => ({
                              ...prev,
                              maxOdds: true,
                            }))
                          }
                          data-testid="create-task-condition-max-odds"
                        >
                          <span
                            className={styles.conditionPillIcon}
                            aria-hidden="true"
                          >
                            +
                          </span>
                          {AppStrings.CREATE_TASK_MAX_ODDS}
                        </button>
                      )}
                      {!visibleConditions.sportSpecific && (
                        <button
                          type="button"
                          className={styles.conditionPill}
                          onClick={() =>
                            setVisibleConditions((prev) => ({
                              ...prev,
                              sportSpecific: true,
                            }))
                          }
                          data-testid="create-task-condition-sport-specific"
                        >
                          <span
                            className={styles.conditionPillIcon}
                            aria-hidden="true"
                          >
                            +
                          </span>
                          {AppStrings.CREATE_TASK_SPORT_SPECIFIC}
                        </button>
                      )}
                      {!visibleConditions.streak && (
                        <button
                          type="button"
                          className={styles.conditionPill}
                          onClick={() =>
                            setVisibleConditions((prev) => ({
                              ...prev,
                              streak: true,
                            }))
                          }
                          disabled={hasNumberOfBets}
                          data-testid="create-task-condition-streak"
                        >
                          <span
                            className={styles.conditionPillIcon}
                            aria-hidden="true"
                          >
                            +
                          </span>
                          {AppStrings.CREATE_TASK_STREAK}
                        </button>
                      )}
                    </div>
                  </>
                )}

                <div className={styles.conditionGrid}>
                  {visibleConditions.minOdds && (
                    <div
                      className={styles.conditionCard}
                      data-testid="create-task-condition-card-min-odds"
                    >
                      <div className={styles.conditionCardHeader}>
                        <span className={styles.fieldLabel}>
                          {AppStrings.CREATE_TASK_MIN_ODDS}
                        </span>
                        {!isViewMode && (
                          <button
                            type="button"
                            className={styles.conditionRemove}
                            aria-label={
                              AppStrings.CREATE_TASK_REMOVE_CONDITION
                            }
                            onClick={() => removeCondition("minOdds")}
                            data-testid="create-task-remove-min-odds"
                          >
                            {"\u2212"}
                          </button>
                        )}
                      </div>
                      <Input
                        value={draft.trigger.oddsRequirement ?? ""}
                        placeholder={minOddsPlaceholder}
                        onChange={(e) =>
                          updateTrigger({
                            oddsRequirement: sanitizeOddsInput(e.target.value),
                          })
                        }
                        disabled={isViewMode}
                        data-testid="create-task-min-odds-input"
                      />
                      <span
                        className={styles.conditionHint}
                        data-testid="create-task-min-odds-hint"
                      >
                        {oddsFormatHint}
                      </span>
                    </div>
                  )}
                  {visibleConditions.maxOdds && (
                    <div
                      className={styles.conditionCard}
                      data-testid="create-task-condition-card-max-odds"
                    >
                      <div className={styles.conditionCardHeader}>
                        <span className={styles.fieldLabel}>
                          {AppStrings.CREATE_TASK_MAX_ODDS}
                        </span>
                        {!isViewMode && (
                          <button
                            type="button"
                            className={styles.conditionRemove}
                            aria-label={
                              AppStrings.CREATE_TASK_REMOVE_CONDITION
                            }
                            onClick={() => removeCondition("maxOdds")}
                            data-testid="create-task-remove-max-odds"
                          >
                            {"\u2212"}
                          </button>
                        )}
                      </div>
                      <Input
                        value={draft.trigger.maxOddsRequirement ?? ""}
                        placeholder={maxOddsPlaceholder}
                        onChange={(e) =>
                          updateTrigger({
                            maxOddsRequirement: sanitizeOddsInput(
                              e.target.value,
                            ),
                          })
                        }
                        disabled={isViewMode}
                        data-testid="create-task-max-odds-input"
                      />
                      <span
                        className={styles.conditionHint}
                        data-testid="create-task-max-odds-hint"
                      >
                        {oddsFormatHint}
                      </span>
                    </div>
                  )}
                  {visibleConditions.sportSpecific && (
                    <div
                      className={`${styles.conditionCard} ${
                        isSportSpecificOnlyCondition
                          ? styles.conditionCardFull
                          : ""
                      }`}
                      data-testid="create-task-condition-card-sport-specific"
                    >
                      <div className={styles.conditionCardHeader}>
                        <span className={styles.fieldLabel}>
                          {AppStrings.CREATE_TASK_SPORT_SPECIFIC}
                        </span>
                        {!isViewMode && (
                          <button
                            type="button"
                            className={styles.conditionRemove}
                            aria-label={
                              AppStrings.CREATE_TASK_REMOVE_CONDITION
                            }
                            onClick={() => removeCondition("sportSpecific")}
                            data-testid="create-task-remove-sport-specific"
                          >
                            {"\u2212"}
                          </button>
                        )}
                      </div>
                      <SportSpecificSelect
                        sports={draft.trigger.sportSpecific ?? []}
                        leagues={draft.trigger.sportLeagues ?? []}
                        onChangeSports={(next) =>
                          updateTrigger({ sportSpecific: next })
                        }
                        onChangeLeagues={(next) =>
                          updateTrigger({ sportLeagues: next })
                        }
                        options={sportOptions}
                        disabled={isViewMode}
                        data-testid="create-task-sport-specific"
                      />
                    </div>
                  )}
                  {visibleConditions.streak && (
                    <div
                      className={styles.conditionCard}
                      data-testid="create-task-condition-card-streak"
                    >
                      <div className={styles.conditionCardHeader}>
                        <span className={styles.fieldLabel}>
                          {AppStrings.CREATE_TASK_STREAK_DAYS}
                        </span>
                        {!isViewMode && (
                          <button
                            type="button"
                            className={styles.conditionRemove}
                            aria-label={
                              AppStrings.CREATE_TASK_REMOVE_CONDITION
                            }
                            onClick={() => removeCondition("streak")}
                            data-testid="create-task-remove-streak"
                          >
                            {"\u2212"}
                          </button>
                        )}
                      </div>
                      <Input
                        value={draft.trigger.streak ?? ""}
                        inputMode="numeric"
                        onChange={(e) =>
                          updateTrigger({
                            streak: sanitizeIntegerInput(e.target.value),
                          })
                        }
                        disabled={isViewMode || hasNumberOfBets}
                        data-testid="create-task-streak-input"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* XP Amount is always visible and lives BELOW the "More
                  Conditions" block (not one of the optional condition
                  cards). Maps to the BE `xp_reward`. */}
              <label className={styles.field}>
                <span className={styles.fieldLabelWithInfo}>
                  {AppStrings.CREATE_TASK_XP_AMOUNT}
                  <span
                    className={styles.infoIcon}
                    title={AppStrings.CREATE_TASK_XP_AMOUNT_INFO}
                    aria-label={AppStrings.CREATE_TASK_XP_AMOUNT_INFO}
                  >
                    i
                  </span>
                </span>
                <Input
                  value={draft.trigger.xpAmount ?? ""}
                  inputMode="numeric"
                  placeholder={AppStrings.CREATE_TASK_XP_AMOUNT_PLACEHOLDER}
                  onChange={(e) =>
                    updateTrigger({
                      xpAmount: sanitizeIntegerInput(e.target.value),
                    })
                  }
                  disabled={isViewMode || isXpLocked}
                  data-testid="create-task-xp-amount"
                />
              </label>
              <SubmissionModeField
                label={
                  AppStrings.CREATE_SEASON_SPORTSBOOK_TASK_SUBMISSION_LABEL
                }
                infoAria={
                  AppStrings.CREATE_SEASON_SPORTSBOOK_TASK_SUBMISSION_INFO_ARIA
                }
                testIdPrefix="create-task"
                fieldId="sportsbook-submission"
                value={sportsbookSubmissionValue}
                disabled={isViewMode}
                onChange={(mode) =>
                  updateTrigger({ taskSubmissionMode: mode })
                }
              />
            </>
          )}

          {/* Casino trigger — provider/game picker + Action toggle. Replaces
              the sportsbook bet fields entirely for `data_source = CASINO`.
              No feature flag here by design: this picker only renders inside the
              already-flag-gated Create Task / casino-task flow, so the parent
              gate is the single source of truth — a second flag here would be a
              dead, always-on duplicate. */}
          {isCasino && (
            <>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>
                  {AppStrings.CREATE_TASK_CASINO_PROVIDERS}
                </span>
                <CasinoProviderSelect
                  providers={draft.trigger.casinoProviders ?? []}
                  games={draft.trigger.casinoGames ?? []}
                  gameLabels={draft.trigger.casinoGameLabels}
                  onChangeProviders={(next) =>
                    updateTrigger({ casinoProviders: next })
                  }
                  onChangeGames={(next) =>
                    updateTrigger({ casinoGames: next })
                  }
                  onChangeGameLabels={(next) =>
                    updateTrigger({ casinoGameLabels: next })
                  }
                  disabled={isViewMode}
                  data-testid="create-task-casino-providers"
                />
              </div>

              <div className={styles.twoColumn}>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>
                    {AppStrings.CREATE_TASK_ACTION}
                  </span>
                  <PillGroup<CasinoAction>
                    value={draft.trigger.casinoAction ?? DEFAULT_CASINO_ACTION}
                    options={CASINO_ACTION_OPTIONS.map((option) => ({
                      value: option.value,
                      label: AppStrings[option.labelKey],
                    }))}
                    onChange={(value) => updateTrigger({ casinoAction: value })}
                    disabled={isViewMode}
                    fullWidth
                    testId="create-task-casino-action"
                  />
                </div>
                <label className={styles.field}>
                  <span className={styles.fieldLabelWithInfo}>
                    {AppStrings.CREATE_TASK_ACTION_AMOUNT}
                    <span
                      className={styles.infoIcon}
                      title={AppStrings.CREATE_TASK_ACTION_AMOUNT_INFO}
                      aria-label={AppStrings.CREATE_TASK_ACTION_AMOUNT_INFO}
                    >
                      i
                    </span>
                  </span>
                  <Input
                    value={draft.trigger.casinoActionAmount ?? ""}
                    inputMode="numeric"
                    placeholder={
                      AppStrings.CREATE_TASK_ACTION_AMOUNT_PLACEHOLDER
                    }
                    onChange={(e) =>
                      updateTrigger({
                        casinoActionAmount: sanitizeIntegerInput(
                          e.target.value,
                        ),
                      })
                    }
                    disabled={isViewMode}
                    data-testid="create-task-casino-action-amount"
                  />
                </label>
              </div>

              <div className={styles.twoColumn}>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>
                    {AppStrings.CREATE_TASK_BET_CURRENCY}
                  </span>
                  <Select<BetCurrency[]>
                    mode="multiple"
                    value={[...(draft.trigger.casinoBetCurrencies ?? [])]}
                    options={betCurrencyOptions}
                    placeholder={
                      AppStrings.CREATE_TASK_BET_CURRENCY_PLACEHOLDER
                    }
                    onChange={(value) =>
                      updateTrigger({ casinoBetCurrencies: value ?? [] })
                    }
                    disabled={isViewMode}
                    data-testid="create-task-casino-bet-currency"
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabelWithInfo}>
                    {AppStrings.CREATE_TASK_WAGER_AMOUNT}
                    <span
                      className={styles.infoIcon}
                      title={AppStrings.CREATE_TASK_WAGER_AMOUNT_INFO}
                      aria-label={AppStrings.CREATE_TASK_WAGER_AMOUNT_INFO}
                    >
                      i
                    </span>
                  </span>
                  <Input
                    value={draft.trigger.casinoWagerAmount ?? ""}
                    inputMode="decimal"
                    placeholder={
                      AppStrings.CREATE_TASK_WAGER_AMOUNT_PLACEHOLDER
                    }
                    onChange={(e) =>
                      updateTrigger({
                        casinoWagerAmount: sanitizeDecimalInput(e.target.value),
                      })
                    }
                    disabled={isViewMode}
                    data-testid="create-task-casino-wager-amount"
                  />
                </label>
              </div>

              {/* XP Amount lives on its own row (mirrors the sportsbook /
                  socials layout). Maps to the BE `xp_reward`. */}
              <label className={styles.field}>
                <span className={styles.fieldLabelWithInfo}>
                  {AppStrings.CREATE_TASK_XP_AMOUNT}
                  <span
                    className={styles.infoIcon}
                    title={AppStrings.CREATE_TASK_XP_AMOUNT_INFO}
                    aria-label={AppStrings.CREATE_TASK_XP_AMOUNT_INFO}
                  >
                    i
                  </span>
                </span>
                <Input
                  value={draft.trigger.xpAmount ?? ""}
                  inputMode="numeric"
                  placeholder={AppStrings.CREATE_TASK_XP_AMOUNT_PLACEHOLDER}
                  onChange={(e) =>
                    updateTrigger({
                      xpAmount: sanitizeIntegerInput(e.target.value),
                    })
                  }
                  disabled={isViewMode || isXpLocked}
                  data-testid="create-task-casino-xp-amount"
                />
              </label>
              <SubmissionModeField
                label={AppStrings.CREATE_SEASON_CASINO_TASK_SUBMISSION_LABEL}
                infoAria={
                  AppStrings.CREATE_TASK_CASINO_TASK_SUBMISSION_INFO_ARIA
                }
                testIdPrefix="create-task"
                fieldId="casino-submission"
                value={CASINO_TASK_SUBMISSION_MODE}
                disabled={isViewMode}
                manualDisabled
              />
            </>
          )}

          {/* Socials trigger — a "Task Type" dropdown + an "X Amount" input,
              plus the always-visible XP Amount. Replaces the sportsbook /
              casino fields entirely for `data_source = SOCIALS`. */}
          {isSocials && (
            <>
              <div className={styles.twoColumn}>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>
                    {AppStrings.CREATE_TASK_SOCIAL_TASK_TYPE_LABEL}
                  </span>
                  <Select<SocialTaskType>
                    value={
                      draft.trigger.socialTaskType ?? DEFAULT_SOCIAL_TASK_TYPE
                    }
                    options={socialTaskTypeOptions}
                    placeholder={
                      AppStrings.CREATE_TASK_SOCIAL_TASK_TYPE_PLACEHOLDER
                    }
                    onChange={(value) =>
                      updateTrigger({
                        socialTaskType: value ?? DEFAULT_SOCIAL_TASK_TYPE,
                      })
                    }
                    disabled={isViewMode}
                    data-testid="create-task-social-task-type"
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabelWithInfo}>
                    {AppStrings.CREATE_TASK_X_AMOUNT}
                    <span
                      className={styles.infoIcon}
                      title={AppStrings.CREATE_TASK_X_AMOUNT_INFO}
                      aria-label={AppStrings.CREATE_TASK_X_AMOUNT_INFO}
                    >
                      i
                    </span>
                  </span>
                  <Input
                    value={draft.trigger.socialAmount ?? ""}
                    inputMode="numeric"
                    placeholder={AppStrings.CREATE_TASK_X_AMOUNT_PLACEHOLDER}
                    onChange={(e) =>
                      updateTrigger({
                        socialAmount: sanitizeIntegerInput(e.target.value),
                      })
                    }
                    disabled={isViewMode}
                    data-testid="create-task-social-amount"
                  />
                </label>
              </div>

              <label className={styles.field}>
                <span className={styles.fieldLabelWithInfo}>
                  {AppStrings.CREATE_TASK_XP_AMOUNT}
                  <span
                    className={styles.infoIcon}
                    title={AppStrings.CREATE_TASK_XP_AMOUNT_INFO}
                    aria-label={AppStrings.CREATE_TASK_XP_AMOUNT_INFO}
                  >
                    i
                  </span>
                </span>
                <Input
                  value={draft.trigger.xpAmount ?? ""}
                  inputMode="numeric"
                  placeholder={AppStrings.CREATE_TASK_XP_AMOUNT_PLACEHOLDER}
                  onChange={(e) =>
                    updateTrigger({
                      xpAmount: sanitizeIntegerInput(e.target.value),
                    })
                  }
                  disabled={isViewMode || isXpLocked}
                  data-testid="create-task-social-xp-amount"
                />
              </label>
            </>
          )}
        </section>
      </div>
    </Modal>
  );
};

// ─── Local pill-group primitive ───────────────────────────────────────────

type PillOption<V extends string> = { value: V; label: string };

type PillGroupProps<V extends string> = {
  value?: V;
  options: ReadonlyArray<PillOption<V>>;
  onChange: (value: V) => void;
  /** When `true` every pill is rendered with `disabled` so clicks are
   *  no-ops and the browser dims the unselected pills automatically.
   *  Used by the modal's `view` mode (read-only preview). */
  disabled?: boolean;
  /** When `true` the group spans the full row and every pill flexes to an
   *  equal share of the width (e.g. the casino Action toggle renders Play /
   *  Win as two 50% halves). Defaults to the compact, content-width pills. */
  fullWidth?: boolean;
  testId: string;
};

function PillGroup<V extends string>({
  value,
  options,
  onChange,
  disabled,
  fullWidth = false,
  testId,
}: PillGroupProps<V>) {
  return (
    <div
      className={`${styles.pillGroup} ${
        fullWidth ? styles.pillGroupFullWidth : ""
      }`}
      role="radiogroup"
      data-testid={testId}
    >
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            disabled={disabled}
            className={`${styles.pill} ${isActive ? styles.pillActive : ""}`}
            onClick={() => onChange(option.value)}
            data-testid={`${testId}-${option.value.toLowerCase()}`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export default CreateTaskModal;
